import React, { lazy, Suspense, useEffect, useState } from 'react';
import '@/App.css';
import Spinner from '@/components/Spinner';
import { BrowserRouter, Routes, Route, useLocation } from 'react-router-dom';
import axios from 'axios';
import { getStoredToken, clearCachedUser } from '@/lib/authCache';
import { useNavegadorDesocupado } from '@/lib/cargaDiferida';
import NotificationPopupManager from '@/components/NotificationPopup';
import { Toaster } from '@/components/ui/sonner';
import ErrorBoundary from '@/components/ErrorBoundary';
import InactivityWarning from '@/components/InactivityWarning';
import { useInactivityLogout } from '@/hooks/useInactivityLogout';
import { GearSix } from '@phosphor-icons/react';

const LandingPage = lazy(() => import('@/pages/LandingPage'));
const EventDisplay = lazy(() => import('@/pages/EventDisplay'));
const PublicFighterProfile = lazy(() => import('@/pages/PublicFighterProfile'));
const CoachProfile = lazy(() => import('@/pages/CoachProfile'));
const CoachDashboard = lazy(() => import('@/pages/CoachDashboard'));
const MisMenores = lazy(() => import('@/pages/MisMenores'));
const ReclamarPerfil = lazy(() => import('@/pages/ReclamarPerfil'));
const AuthCallback = lazy(() => import('@/pages/AuthCallback'));
const Login = lazy(() => import('@/pages/Login'));
const Register = lazy(() => import('@/pages/Register'));
const Dashboard = lazy(() => import('@/pages/Dashboard'));
const CreateProfile = lazy(() => import('@/pages/CreateProfile'));
const SwipeInterface = lazy(() => import('@/pages/SwipeInterface'));
const AdminDashboard = lazy(() => import('@/pages/AdminDashboard'));
const FightCard = lazy(() => import('@/pages/FightCard'));
const MyMatches = lazy(() => import('@/pages/MyMatches'));
const Events = lazy(() => import('@/pages/Events'));
const CreateEvent = lazy(() => import('@/pages/CreateEvent'));
const EventDetail = lazy(() => import('@/pages/EventDetail'));
const EventBrackets = lazy(() => import('@/pages/EventBrackets'));
const EditProfile = lazy(() => import('@/pages/EditProfile'));
const EditEvent = lazy(() => import('@/pages/EditEvent'));
const PublicResults = lazy(() => import('@/pages/PublicResults'));
const OrganizerDashboard = lazy(() => import('@/pages/OrganizerDashboard'));
const ForgotPassword = lazy(() => import('@/pages/ForgotPassword'));
const Contact = lazy(() => import('@/pages/Contact'));
const Manual = lazy(() => import('@/pages/Manual'));
const AccountSettings = lazy(() => import('@/pages/AccountSettings'));
const MatchChat = lazy(() => import('@/pages/MatchChat'));
const FightHistory = lazy(() => import('@/pages/FightHistory'));
const OrganizerProfile = lazy(() => import('@/pages/OrganizerProfile'));
const Terms = lazy(() => import('@/pages/Terms'));
const Privacidad = lazy(() => import('@/pages/Privacidad'));
const Cookies = lazy(() => import('@/pages/Cookies'));
const WebpayReturn = lazy(() => import('@/pages/WebpayReturn'));
const Rankings = lazy(() => import('@/pages/Rankings'));
const MyRanking = lazy(() => import('@/pages/MyRanking'));
const Votacion = lazy(() => import('@/pages/Votacion'));
const Votaciones = lazy(() => import('@/pages/Votaciones'));
const MyEvents = lazy(() => import('@/pages/MyEvents'));
const StaffRedeem = lazy(() => import('@/pages/StaffRedeem'));
const StaffStation = lazy(() => import('@/pages/StaffStation'));
const RingDisplay = lazy(() => import('@/pages/RingDisplay'));

const PageLoader = () => (
  <div className="min-h-screen flex items-center justify-center bg-black">
    <Spinner size={32} />
  </div>
);

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
export const API = `${BACKEND_URL}/api`;

// Interceptor global: agrega Bearer token a todas las llamadas al backend.
// Necesario para Safari iOS / mobile que bloquean cookies cross-origin (ITP).
axios.interceptors.request.use((config) => {
  if (config.url && config.url.startsWith(BACKEND_URL)) {
    const token = getStoredToken();
    if (token && !config.headers['Authorization']) {
      config.headers['Authorization'] = `Bearer ${token}`;
    }
  }
  return config;
});

let _sessionDisplacedHandled = false;
axios.interceptors.response.use(
  (response) => response,
  (error) => {
    if (error.response?.status === 401 && error.response?.data?.detail === 'SESSION_DISPLACED') {
      if (!_sessionDisplacedHandled && !window.location.pathname.startsWith('/login')) {
        _sessionDisplacedHandled = true;
        clearCachedUser();
        window.location.href = '/login?reason=session_displaced';
      }
    }
    return Promise.reject(error);
  }
);

function ScrollToTop() {
  const { pathname } = useLocation();
  useEffect(() => { window.scrollTo(0, 0); }, [pathname]);
  return null;
}

function InactivityManager() {
  const [showWarning, setShowWarning] = useState(false);
  const { doLogout, resetTimers } = useInactivityLogout({
    onWarn: () => setShowWarning(true),
    onLogout: () => setShowWarning(false),
  });

  const handleStay = () => {
    setShowWarning(false);
    resetTimers();
  };

  return showWarning ? (
    <InactivityWarning onStay={handleStay} onLogout={doLogout} />
  ) : null;
}

function AppRouter() {
  const location = useLocation();

  if (location.hash?.includes('session_id=')) {
    return <AuthCallback />;
  }

  return (
    <ErrorBoundary key={location.pathname}>
    <Suspense fallback={<PageLoader />}>
      <Routes>
        <Route path="/" element={<LandingPage />} />
        <Route path="/login" element={<Login />} />
        <Route path="/register" element={<Register />} />
        <Route path="/forgot-password" element={<ForgotPassword />} />
        {/* La direccion que trae el enlace del correo de recuperacion. Faltaba, y el
            enlace llevaba a una pagina en blanco. */}
        <Route path="/reset-password" element={<ForgotPassword />} />
        <Route path="/dashboard" element={<Dashboard />} />
        <Route path="/create-profile" element={<CreateProfile />} />
        <Route path="/swipe" element={<SwipeInterface />} />
        <Route path="/admin" element={<AdminDashboard />} />
        <Route path="/fight-card" element={<FightCard />} />
        <Route path="/my-matches" element={<MyMatches />} />
        <Route path="/events" element={<Events />} />
        <Route path="/events/create" element={<CreateEvent />} />
        <Route path="/events/:eventId" element={<EventDetail />} />
        <Route path="/events/:eventId/brackets" element={<EventBrackets />} />
        <Route path="/events/:eventId/edit" element={<EditEvent />} />
        <Route path="/edit-profile" element={<EditProfile />} />
        <Route path="/results/:eventId" element={<PublicResults />} />
        <Route path="/events/:eventId/display" element={<EventDisplay />} />
        <Route path="/events/:eventId/tv" element={<RingDisplay />} />
        <Route path="/atleta/:fighterId" element={<PublicFighterProfile />} />
        <Route path="/coach/profile" element={<CoachProfile />} />
        <Route path="/coach/dashboard" element={<CoachDashboard />} />
        <Route path="/tutor" element={<MisMenores />} />
        <Route path="/reclamar/:token" element={<ReclamarPerfil />} />
        <Route path="/organizer" element={<OrganizerDashboard />} />
        <Route path="/contact" element={<Contact />} />
        <Route path="/manual" element={<Manual />} />
        <Route path="/account" element={<AccountSettings />} />
        <Route path="/chat/:matchId" element={<MatchChat />} />
        <Route path="/fight-history" element={<FightHistory />} />
        <Route path="/organizer/profile" element={<OrganizerProfile />} />
        <Route path="/terms" element={<Terms />} />
        <Route path="/privacidad" element={<Privacidad />} />
        <Route path="/cookies" element={<Cookies />} />
        <Route path="/ranking" element={<Rankings />} />
        <Route path="/mi-ranking" element={<MyRanking />} />
        <Route path="/popularidad" element={<Votaciones />} />
        <Route path="/votaciones/:campaignId" element={<Votacion />} />
        <Route path="/my-events" element={<MyEvents />} />
        <Route path="/staff/station" element={<StaffStation />} />
        <Route path="/staff/:code" element={<StaffRedeem />} />
        <Route path="/api/webpay/return" element={<WebpayReturn />} />
      </Routes>
    </Suspense>
    </ErrorBoundary>
  );
}

/* ── Página de mantención ── */
const MaintenancePage = ({ message }) => (
  <div className="min-h-screen bg-[#050505] text-white flex flex-col items-center justify-center p-8"
    style={{ backgroundImage: 'radial-gradient(ellipse at 50% 30%, rgba(255,107,0,0.07) 0%, transparent 60%)' }}>
    <GearSix size={72} weight="bold" className="text-[#FF6B00] mb-6 animate-spin" style={{ animationDuration: '4s' }} />
    <h1 className="text-4xl sm:text-6xl font-heading uppercase tracking-tight mb-4 text-center">
      En <span className="text-[#FF6B00]">Mantención</span>
    </h1>
    <p className="text-[#A3A3A3] text-center max-w-md text-sm leading-relaxed">
      {message || 'Estamos realizando mejoras en la plataforma. Vuelve en unos minutos.'}
    </p>
    <p className="text-[#666] text-xs mt-8 uppercase tracking-widest">Combat Connect</p>
  </div>
);

function App() {
  // Arranca en false —operativo— y no en null.
  //
  // Antes esto empezaba en null y mas abajo se devolvia un spinner mientras tanto,
  // asi que la aplicacion COMPLETA quedaba en blanco hasta que respondiera
  // /settings/app. Con el backend en el plan gratuito de Render, que se apaga sin
  // trafico, esa primera consulta despierta el proceso y puede tardar bastante:
  // el visitante nuevo miraba un spinner sin nada mas, aunque la portada es texto
  // e imagenes que ya venian en el bundle.
  //
  // Ahora la pagina se pinta de inmediato y el modo mantenimiento se comprueba por
  // detras. Si esta activo, la pantalla de mantenimiento aparece un segundo
  // despues; se prefiere eso a dejar a todo el mundo esperando por un modo que
  // casi nunca esta encendido.
  const [maintenance, setMaintenance] = useState(false); // false=ok, string=mensaje

  // Ademas de no bloquear, la comprobacion espera a que el navegador quede
  // desocupado: asi no compite con la primera pintura.
  const navegadorDesocupado = useNavegadorDesocupado();

  useEffect(() => {
    if (!navegadorDesocupado) return;
    const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
    axios.get(`${BACKEND_URL}/api/settings/app`).then(res => {
      if (res.data.maintenance_mode) {
        setMaintenance(res.data.maintenance_message || '');
      }
    }).catch(() => {}); // si falla, deja pasar
  }, [navegadorDesocupado]);

  // Detectar si el token es de admin para no bloquearles la entrada
  const isAdmin = (() => {
    try {
      const token = getStoredToken();
      if (!token) return false;
      const payload = JSON.parse(atob(token.split('.')[1]));
      return payload.role === 'admin';
    } catch { return false; }
  })();

  if (maintenance !== false && !isAdmin) {
    return <MaintenancePage message={maintenance} />;
  }

  return (
    <div className="App">
      <BrowserRouter>
        <ScrollToTop />
        <InactivityManager />
        <NotificationPopupManager />
        <AppRouter />
      </BrowserRouter>
      <Toaster position="top-center" richColors />
    </div>
  );
}

export default App;